import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import useDuplicateVisitMutation from "../../hooks/useDuplicateVisitMutation";
import { showApiErrorToast } from "../../utils/helpers";

const MAX_PREFIX_LENGTH = 200;

const formatHours = (totalHours) => {
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return hours ? `Day ${days}, Hour ${hours}` : `Day ${days}`;
};

const toWholeNumber = (value) => (value === "" ? null : Number(value));

const DuplicateVisitDialog = ({
  open,
  onClose,
  trialId,
  sourceVisit,
  scheduleAnchor,
  onDuplicated,
}) => {
  const [count, setCount] = useState("");
  const [startNumber, setStartNumber] = useState("");
  const [namePrefix, setNamePrefix] = useState("Visit");

  const duplicateVisitMutation = useDuplicateVisitMutation();

  useEffect(() => {
    if (open) {
      setCount("");
      setStartNumber("");
      setNamePrefix("Visit");
    }
  }, [open]);

  const countValue = toWholeNumber(count);
  const startValue = toWholeNumber(startNumber);
  const prefix = namePrefix.trim();
  const sourceDuration = Number(sourceVisit?.end_visit_schedule) || 0;

  const countError = count !== "" && (!Number.isInteger(countValue) || countValue < 1);
  const startError = startNumber !== "" && (!Number.isInteger(startValue) || startValue < 0);
  const prefixError = namePrefix.length > 0 && prefix.length === 0;
  const durationError = sourceDuration <= 0;

  const isValid =
    Number.isInteger(countValue) &&
    countValue >= 1 &&
    Number.isInteger(startValue) &&
    startValue >= 0 &&
    prefix.length > 0 &&
    !durationError;

  const handleSubmit = async () => {
    try {
      const result = await duplicateVisitMutation.mutateAsync({
        trialId,
        visitNumber: sourceVisit.visit_number,
        count: countValue,
        startNumber: startValue,
        namePrefix: prefix,
      });
      toast.success(`Created ${result.created} visits.`);
      onDuplicated?.(result);
      onClose();
    } catch (error) {
      showApiErrorToast(error, "Failed to duplicate visit.");
    }
  };

  const isSubmitting = duplicateVisitMutation.isPending;

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Duplicate {sourceVisit?.visit_name || "Visit"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Creates copies of this visit, with the same eCRFs, modules and settings. Copies are
            added after the last visit, each starting right after the previous one.
          </Typography>

          {durationError && (
            <Typography variant="body2" color="error">
              This visit needs a duration greater than zero before it can be duplicated.
            </Typography>
          )}

          <TextField
            label="Number of copies"
            type="number"
            size="small"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            inputProps={{ min: 1, step: 1, "data-testid": "duplicate-visit-count-input" }}
            error={countError}
            helperText={countError ? "Enter a whole number of at least 1" : ""}
            disabled={isSubmitting}
            autoFocus
          />

          <TextField
            label="Start numbering from"
            type="number"
            size="small"
            value={startNumber}
            onChange={(e) => setStartNumber(e.target.value)}
            inputProps={{ min: 0, step: 1, "data-testid": "duplicate-visit-start-number-input" }}
            error={startError}
            helperText={startError ? "Enter a whole number, 0 or more" : ""}
            disabled={isSubmitting}
          />

          <TextField
            label="Name prefix"
            size="small"
            value={namePrefix}
            onChange={(e) => setNamePrefix(e.target.value)}
            inputProps={{
              maxLength: MAX_PREFIX_LENGTH,
              "data-testid": "duplicate-visit-prefix-input",
            }}
            error={prefixError}
            helperText={prefixError ? "Prefix cannot be blank" : ""}
            disabled={isSubmitting}
          />

          {isValid && (
            <Typography variant="body2" data-testid="duplicate-visit-summary">
              Creates {countValue} {countValue === 1 ? "visit" : "visits"}: {prefix} {startValue} to{" "}
              {prefix} {startValue + countValue - 1}
              <br />
              Starts {formatHours(scheduleAnchor)}
              {countValue > 1 &&
                `, last starts ${formatHours(scheduleAnchor + (countValue - 1) * sourceDuration)}`}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          data-testid="duplicate-visit-submit-btn"
          disabled={!isValid || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Duplicate
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DuplicateVisitDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trialId: PropTypes.number,
  sourceVisit: PropTypes.object,
  scheduleAnchor: PropTypes.number,
  onDuplicated: PropTypes.func,
};

export default DuplicateVisitDialog;
